<template>
  <main
    class="article page-main"
  >
    <section v-if="desserts.writingImagePath" ref="topImg" class="article-visual">
      <img
        ref="visual"
        :src="desserts.writingImagePath"
        alt=""
        @load="imageLoad"
      >
    </section>

    <section class="article-detail">
      <div class="article-detail-heading">
        <div class="article-detail-meta">
          <span class="article-detail-category" @click.prevent="goCategory">{{
            isKorean ?
              desserts.writingCategoryKoreaName :
              desserts.writingCategoryEnglishName
          }}</span>
          <time class="article-detail-date" :datetime="$moment(desserts.writingPublishDate).format('YY.MM.DD')">{{
            $moment(desserts.writingPublishDate).format('YY.MM.DD')
          }}
          </time>
        </div>
        <div class="article-detail-title">
          <strong>{{ desserts.writingTitle }}</strong>
          <small v-if="desserts.writingSubTitle">{{ desserts.writingSubTitle }}</small>
        </div>
      </div>

      <loading v-if="loading" />

      <div v-else class="article-detail-content">
        <div v-if="desserts.summaries" class="article-detail-summary">
          <ul v-for="item in desserts.summaries" :key="item.writingSummarySeq">
            <li>{{ item.writingSummaryContents }}</li>
          </ul>
        </div>

        <html-view v-model="desserts.content" class="editor" />
      </div>

      <div class="share-wrap">
        <share
          :seq="Number($route.params.writingId)"
          :title="desserts.writingTitle"
          :type="'WRITING'"
          :share-image="desserts.writingImagePath"
        />
      </div>
    </section>

    <section v-if="desserts.recommendWritings && desserts.recommendWritings.length > 0" class="relate">
      <p class="relate-title">{{ $t('detail.wantNext') }}</p>
      <div class="relate-content">
        <div ref="relate" class="swiper">
          <div class="list list-grid">
            <div
              v-for="item in desserts.recommendWritings"
              :key="item.writingSeq"
              class="list-box"
            >
              <router-link
                :to="{ name: 'writingDetail' , params : { locale: $route.params.locale, category : $route.params.category, writingId : item.writingSeq}}"
              >
                <div class="list-thumb"><img :src="imgPath(item.writingImagePath)" alt="" @error.once="onImageError"></div>
                <div class="list-cont">
                  <p class="list-title">{{ item.writingTitle }}</p>
                  <p class="list-desc">{{ item.writingSubTitle }}</p>
                  <div>
                    <span class="list-category">{{
                      isKorean ?
                        item.writingCategoryKoreaName :
                        item.writingCategoryEnglishName
                    }}</span>
                  </div>
                </div>
              </router-link>
            </div>
          </div>
          <div class="swiper-navigation">
            <button type="button" class="swiper-button-prev">
              <icon-chevron-left />
            </button>
            <button type="button" class="swiper-button-next">
              <icon-chevron-right />
            </button>
          </div>
        </div>
      </div>
    </section>
    <div class="article-detail-btn">
      <button type="button" class="btn" @click.prevent="goList">{{ $t('seeList') }}</button>
    </div>
  </main>
</template>
<script>
import Swiper, { Navigation } from 'swiper'
import writingApi from '@/api/writing'
import { getAverageRGB } from '@/util'
import observer from '@/mixins/observer'
import animation from '@/mixins/animation'
import isKorean from '@/mixins/isKorean'
import contentDetail from '@/mixins/contentDetail'
import { mapGetters } from 'vuex'

Swiper.use([Navigation])

export default {
  components: {
    share: () => import('@/components/base/Share'),
    iconChevronLeft: () => import('@/static/svg/chevron-left-big.svg'),
    iconChevronRight: () => import('@/static/svg/chevron-right-big.svg'),
    loading: () => import('@/components/base/Loading')
  },
  mixins: [isKorean, observer, animation, contentDetail],
  data () {
    return {
      loading: false,
      desserts: {},
      defaultImgPath: require('@/static/image/default.jpg'),
      slider: null
    }
  },
  computed: {
    ...mapGetters({
      writingCategories: 'categories/get_writing_categories'
    })
  },
  watch: {
    '$route.params.writingId' () {
      this.getDetail()
    },
    '$route.params.locale' () {
      if (this.slider) {
        this.slider.destroy()
        this.slider = null
      }

      this.getDetail()
    }
  },
  mounted () {
    if (this.$route.query.sharekey) {
      this.getShareDetail()
    } else {
      this.getDetail()
    }
  },
  methods: {
    imgPath (value) {
      return value || this.defaultImgPath
    },
    goCategory () {
      const catagoryIndex = this.writingCategories.find((v, i) => {
        return v.categoryKoreaName === this.desserts.writingCategoryKoreaName
      })

      this.$router.push({
        name: 'Writing',
        params: {
          category: catagoryIndex.categorySeq,
          locale: this.$route.params.locale
        },
        query: { page: 1 }
      })
    },
    goList () {
      document.documentElement.scrollTop = 0
      this.$router.push({
        name: 'Writing',
        params: {
          category: this.$route.params.category,
          locale: this.$route.params.locale
        },
        query: { page: 1 }
      })
    },
    async getDetail () {
      this.loading = true
      const { data } = await writingApi.getWritingDetail(this.$route.params.writingId, {
        language: this.isKorean ? 'KR' : 'EN'
      })
      if (data) {
        this.desserts = data
        this.$nextTick(this.setSlider)

        this.setContents()
      }
      this.loading = false
    },
    async getShareDetail () {
      this.loading = true
      const { data } = await writingApi.getWritingLink({
        linkKey: this.$route.query.sharekey,
        language: this.isKorean ? 'KR' : 'EN'
      })
      if (data) {
        this.desserts = data
        this.$nextTick(this.setSlider)
        this.setContents()
      }
      this.loading = false
    },
    imageLoad () {
      const callback = (style) => {
        this.$refs.topImg.dataset.style = style
        this.initObserver(this.$el.querySelectorAll('section'))
      }
      getAverageRGB(this.$refs.visual, callback)
    },
    setSlider: function () {
      if (this.desserts.recommendWritings && this.desserts.recommendWritings.length === 0) {
        return false
      }

      this.slider = new Swiper(this.$refs.relate, {
        loop: false,
        // autoplay: false,
        // centeredSlides: true,
        // centerInsufficientSlides: true,
        slideClass: 'list-box',
        wrapperClass: 'list-grid',
        pagination: false,
        simulateTouch: false,
        // spaceBetween: 15,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        breakpoints: {
          280: {
            loop: true,
            slidesPerGroup: 1,
            slidesPerView: 1
          },
          750: {
            loop: false,
            simulateTouch: true,
            slidesPerGroup: 3,
            slidesPerView: 3
            // navigation: false
          }
        }
      })
    },
    onImageError (e) {
      e.target.src = this.defaultImgPath
    }
  }
}
</script>
